import { Input, Button } from 'antd';
import {
  UserOutlined,
  LockOutlined,
  BankOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
} from '@ant-design/icons';
import {
  Controller,
  Control,
  FieldErrors,
  UseFormTrigger,
  UseFormGetValues,
} from 'react-hook-form';
import { useNotification } from '@/shared/utils/notification';
import { authApi } from '@/api';
import { UI_TEXT } from '../constants/index';
import { type LoginFormData } from '../schemas';
import React from 'react';

interface LoginFormProps {
  control: Control<LoginFormData>;
  errors: FieldErrors<LoginFormData>;
  trigger: UseFormTrigger<LoginFormData>;
  getValues: UseFormGetValues<LoginFormData>;
  isLoading: boolean;
  error: string | null;
  disabled?: boolean;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  onClearError: () => void;
}

/**
 * Login Form Component
 * Receives form control and errors from parent
 * Pure presentational component - validation logic handled by parent
 */
export const LoginForm: React.FC<LoginFormProps> = ({
  control,
  errors,
  trigger,
  getValues,
  isLoading,
  disabled = false,
  onSubmit,
}) => {
  const { showToast } = useNotification();

  /**
   * Handle forgot password click
   * Validates companyCode and userId fields and sends reset request to admin
   */
  const handleForgotPassword = async () => {
    // Trigger validation for companyCode and userId fields only
    const isValid = await trigger(['companyCode', 'userId']);

    if (isValid) {
      try {
        // Get current form values (not using field names array to avoid type issues)
        const formData = getValues();
        await authApi.forgotPassword({
          companyCode: formData.companyCode,
          userId: formData.userId,
        });

        showToast({
          type: 'success',
          title: 'Request Sent',
          message: 'Password reset request has been sent to administrator',
        });
      } catch (error: any) {
        showToast({
          type: 'error',
          title: 'Failed',
          message:
            error?.response?.data?.message || 'Failed to send reset request',
        });
      }
    }
    // If not valid, errors will automatically show on the input fields
  };

  return (
    <form onSubmit={onSubmit} autoComplete="off">
      {/* Company Code */}
      <div style={{ marginBottom: '12px' }}>
        <label
          htmlFor="companyCode"
          style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: 500,
          }}
        >
          {UI_TEXT.COMPANY_CODE_LABEL}
        </label>
        <Controller
          name="companyCode"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              id="companyCode"
              prefix={<BankOutlined />}
              placeholder="Enter company code"
              autoComplete="organization"
              disabled={isLoading || disabled}
              size="large"
              status={errors.companyCode ? 'error' : ''}
            />
          )}
        />
        {/* Reserve space for error message to prevent UI jumping */}
        <div style={{ minHeight: '22px', marginTop: '4px' }}>
          {errors.companyCode && (
            <div style={{ color: '#ff4d4f', fontSize: '14px' }}>
              {errors.companyCode.message}
            </div>
          )}
        </div>
      </div>

      {/* User ID */}
      <div style={{ marginBottom: '12px' }}>
        <label
          htmlFor="userId"
          style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: 500,
          }}
        >
          {UI_TEXT.USER_ID_LABEL}
        </label>
        <Controller
          name="userId"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              id="userId"
              prefix={<UserOutlined />}
              placeholder="Enter user ID"
              autoComplete="username"
              disabled={isLoading || disabled}
              size="large"
              status={errors.userId ? 'error' : ''}
            />
          )}
        />
        {/* Reserve space for error message to prevent UI jumping */}
        <div style={{ minHeight: '22px', marginTop: '4px' }}>
          {errors.userId && (
            <div style={{ color: '#ff4d4f', fontSize: '14px' }}>
              {errors.userId.message}
            </div>
          )}
        </div>
      </div>

      {/* Password */}
      <div style={{ marginBottom: '12px' }}>
        <label
          htmlFor="password"
          style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: 500,
          }}
        >
          {UI_TEXT.PASSWORD_LABEL}
        </label>
        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <Input.Password
              {...field}
              id="password"
              prefix={<LockOutlined />}
              placeholder="Enter password"
              autoComplete="current-password"
              iconRender={visible =>
                visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
              }
              disabled={isLoading || disabled}
              size="large"
              status={errors.password ? 'error' : ''}
            />
          )}
        />
        {/* Reserve space for error message to prevent UI jumping */}
        <div style={{ minHeight: '22px', marginTop: '4px' }}>
          {errors.password && (
            <div style={{ color: '#ff4d4f', fontSize: '14px' }}>
              {errors.password.message}
            </div>
          )}
        </div>
      </div>

      {/* Remember Me & Forgot Password */}
      <div style={{ marginBottom: '24px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>{/* Remember me placeholder */}</div>
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={handleForgotPassword}
            disabled={isLoading || disabled}
          >
            {UI_TEXT.FORGOT_PASSWORD}
          </Button>
        </div>
      </div>

      {/* Submit Button */}
      <Button
        type="primary"
        htmlType="submit"
        block
        loading={isLoading}
        disabled={disabled}
        size="large"
        style={{
          height: '48px',
          fontSize: '16px',
          fontWeight: 500,
          marginBottom: '16px',
        }}
      >
        {isLoading ? UI_TEXT.SUBMITTING_BUTTON : UI_TEXT.SUBMIT_BUTTON}
      </Button>
    </form>
  );
};
